// frontend/src/components/notifications/NotificationContainer.jsx
import React, { useEffect, useRef } from 'react';
import { ToastContainer } from 'react-bootstrap';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationToast from './NotificationToast';
import { shouldShowToast, markToastAsShown } from '../../utils/NotificationPriorityManager';

/**
 * 🗂️ CONTAINER PRINCIPAL DE NOTIFICAÇÕES
 * 
 * Gerencia a exibição de múltiplas notificações toast
 * em posição fixa na tela com sistema de prioridade.
 * 
 * REGRA: Apenas notificações de ALTA PRIORIDADE aparecem como toast.
 */

const NotificationContainer = ({ position = 'top-end' }) => {
  const { notifications, removeNotification } = useNotifications();
  const shownNotificationsRef = useRef(new Set());

  // 🎯 Filtrar APENAS notificações de ALTA PRIORIDADE que ainda não foram mostradas
  const toastNotifications = notifications.filter(notification => {
    // 🚫 Não mostrar toasts para notificações carregadas do servidor (carga inicial)
    if (notification.fromServer) {
      return false;
    }
    
    // Se já foi mostrado nesta sessão, não mostrar novamente
    if (shownNotificationsRef.current.has(notification.id)) {
      return false;
    }
    
    // Verificar se deve mostrar toast (prioridade alta + não mostrado recentemente)
    return shouldShowToast(notification);
  }).slice(0, 3); // Máximo 3 toasts simultâneos

  // Marcar notificações como mostradas
  useEffect(() => {
    toastNotifications.forEach(notification => {
      if (!shownNotificationsRef.current.has(notification.id)) {
        shownNotificationsRef.current.add(notification.id);
        markToastAsShown(notification.id);
        console.log(`🔔 Toast exibido para notificação de alta prioridade: ${notification.type}`);
      }
    });
  }, [toastNotifications]);

  const getPositionStyle = () => {
    const positions = {
      'top-start': { top: '20px', left: '20px' },
      'top-center': { top: '20px', left: '50%', transform: 'translateX(-50%)' },
      'top-end': { top: '20px', right: '20px' },
      'middle-start': { top: '50%', left: '20px', transform: 'translateY(-50%)' },
      'middle-center': { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
      'middle-end': { top: '50%', right: '20px', transform: 'translateY(-50%)' },
      'bottom-start': { bottom: '20px', left: '20px' },
      'bottom-center': { bottom: '20px', left: '50%', transform: 'translateX(-50%)' },
      'bottom-end': { bottom: '20px', right: '20px' }
    };

    return positions[position] || positions['top-end'];
  };

  if (toastNotifications.length === 0) {
    return null;
  }

  return (
    <ToastContainer
      position="fixed"
      style={{
        zIndex: 9999,
        ...getPositionStyle()
      }}
    >
      {toastNotifications.map(notification => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onClose={removeNotification}
        />
      ))}
    </ToastContainer>
  );
};

export default NotificationContainer;
























